import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import {
    Route,
    withRouter,
    Switch,
    Redirect
} from 'react-router-dom';
import loadable from '@loadable/component';
import { TrackingContext } from '@vrbo/react-event-tracking';
import { getTemplate } from './templates/TemplateHelper';
import BedAndBathComponent from '../BedAndBath';
import Location from './Location/AutoComplete';
import ProtectedRoute, { AUTH_FLOW_TYPES } from './ProtectedRoute';
import LocaleRoute from './LocaleRoute';
import Classification from './Classification';
import TranslationUtil from '../../utils/TranslationUtil';
import LegalFooter from '../LegalFooter';
import { requiresLegalFooter } from '../../utils/LocaleUtil/countryList';
import EligibilityPage from './EligibilityCatch';
import reasons from './EligibilityCatch/reasons';
import AppActions from '../../actions/app';
import { pathSources, templates } from '../../constants/common';
import LYPContext from './LYPContext';
import JoinCreation from '../JoinCreation';
import AgenticJoinCreation from '../JoinCreation/AgenticJoinCreation';
import VrPropertyCreation from '../VrPropertyCreation';
import JoinRedirect from '../JoinRedirect';

/* istanbul ignore next */
const ManualLocation = loadable(() => import('./Location/ManualLocation'));
/* istanbul ignore next */
const ConfirmLocation = loadable(() => import('./Location/ConfirmLocation'));
/* istanbul ignore next */
const LeadForm = loadable(() => import('./LeadForm'));
/* istanbul ignore next */
const EstimateResults = loadable(() => import('./EstimateResults'));
/* istanbul ignore next */
const MissingDetails = loadable(() => import('./MissingDetails'));
/* istanbul ignore next */
const LoginInterstitial = loadable(() => import('./LoginInterstitial'));
/* istanbul ignore next */
const UpdateAccount = loadable(() => import('./UpdateAccount'));
/* istanbul ignore next */
const PropertyDetails = loadable(() => import('./PropertyDetails'));
/* istanbul ignore next */
const LanguageSelection = loadable(() => import('./LanguageSelection'));

export function ListYourPropertyPage(props) {
    const { trigger } = useContext(TrackingContext);
    const {
        Template,
        rootPath,
        features,
        templateId,
        legalFooterText,
        legalFooterTooltip,
        showLegalFooter,
        pathSource,
        setPathSource,
        history
    } = props;

    useEffect(() => {
        trigger('genericEvent', {
            category: 'navigation',
            action: 'load-lyp'
        });

        return () => {
            trigger('genericEvent', {
                category: 'navigation',
                action: 'unload-lyp'
            });
        };
    }, []);

    useEffect(() => {
        setPathSource(pathSource);
    }, [pathSource]);

    return (
        <LYPContext history={history}>
            <Template {...props}>
                <Switch>
                    <LocaleRoute exact path={`/:locale/${rootPath}`}>
                        {
                            // eslint-disable-next-line no-nested-ternary
                            features.isLocationFirst
                                ? templateId === 'eg' ? <Location {...props} /> : <Classification {...props} />
                                : <BedAndBathComponent {...props} />
                        }
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/property-details`}>
                        <BedAndBathComponent {...props} />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/property-info`}>
                        <PropertyDetails {...props} />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/classification`}>
                        <Classification {...props} />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/languageSelection`}>
                        <LanguageSelection {...props} />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/location`}>
                        <Location {...props} />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/manual-location`}>
                        <ManualLocation {...props} />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/confirm-location`}>
                        <ConfirmLocation {...props} />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/lead`}>
                        <LeadForm {...props} />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/eligibility/vrbo`}>
                        <EligibilityPage
                            {
                                ...{
                                    ...props,
                                    eligibilityReason: reasons.VRBO
                                }
                            }
                        />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/eligibility/size`}>
                        <EligibilityPage
                            {
                                ...{
                                    ...props,
                                    eligibilityReason: reasons.SIZE_LOCATION
                                }
                            }
                        />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/eligibility/location`}>
                        <EligibilityPage
                            {
                                ...{
                                    ...props,
                                    eligibilityReason: reasons.LOCATION
                                }
                            }
                        />
                    </LocaleRoute>
                    <LocaleRoute path={`/:locale/${rootPath}/eligibility/add-property`}>
                        <EligibilityPage
                            {
                                ...{
                                    ...props,
                                    eligibilityReason: reasons.ADD_PROPERTY
                                }
                            }
                        />
                    </LocaleRoute>
                    <LocaleRoute path={`/:locale/${rootPath}/join`}>
                        <JoinRedirect {...props} />
                    </LocaleRoute>
                    <ProtectedRoute exact path={`/:locale/${rootPath}/update-account`} protectedType="traveler" {...props}>
                        <UpdateAccount {...props} />
                    </ProtectedRoute>
                    <ProtectedRoute exact path={`/:locale/${rootPath}/login-interstitial`} protectedType="traveler" {...props}>
                        <LoginInterstitial {...props} />
                    </ProtectedRoute>
                    <ProtectedRoute exact path={`/:locale/${rootPath}/missing-details`} protectedType="traveler" {...props}>
                        <MissingDetails {...props} />
                    </ProtectedRoute>
                    <ProtectedRoute exact path={`/:locale/${rootPath}/estimate-results`} {...props}>
                        <EstimateResults {...props} />
                    </ProtectedRoute>
                    <ProtectedRoute exact path={`/:locale/${rootPath}/property-creation`} {...props}>
                        <VrPropertyCreation {...props} />
                    </ProtectedRoute>
                    <ProtectedRoute
                        exact
                        path={`/:locale/${rootPath}/add-property/property-info`}
                        {...{
                            ...props,
                            authFlow: AUTH_FLOW_TYPES.ADD_PROPERTY
                        }}
                    >
                        <PropertyDetails {...{
                            ...props,
                            isAAP: true
                        }}
                        />
                    </ProtectedRoute>
                    <ProtectedRoute
                        exact
                        path={`/:locale/${rootPath}/add-property/manual-location`}
                        {...{
                            ...props,
                            authFlow: AUTH_FLOW_TYPES.ADD_PROPERTY
                        }}
                    >
                        <ManualLocation {...{
                            ...props,
                            isAAP: true
                        }}
                        />
                    </ProtectedRoute>
                    <ProtectedRoute
                        exact
                        path={`/:locale/${rootPath}/add-property/confirm-location`}
                        {...{
                            ...props,
                            authFlow: AUTH_FLOW_TYPES.ADD_PROPERTY
                        }}
                    >
                        <ConfirmLocation {...{
                            ...props,
                            isAAP: true
                        }}
                        />
                    </ProtectedRoute>
                    <ProtectedRoute
                        exact
                        path={`/:locale/${rootPath}/add-property/location`}
                        {...{
                            ...props,
                            authFlow: AUTH_FLOW_TYPES.ADD_PROPERTY
                        }}
                    >
                        <Location {...{
                            ...props,
                            isAAP: true
                        }}
                        />
                    </ProtectedRoute>
                    <ProtectedRoute
                        exact
                        path={`/:locale/${rootPath}/add-property/join-creation`}
                        {...{
                            ...props,
                            authFlow: AUTH_FLOW_TYPES.ADD_PROPERTY
                        }}
                    >
                        <JoinCreation {...{
                            ...props,
                            isAAP: true
                        }}
                        />
                    </ProtectedRoute>
                    <ProtectedRoute
                        exact
                        path={`/:locale/${rootPath}/join-creation`}
                        {...{
                            ...props,
                            authFlow: AUTH_FLOW_TYPES.UPL
                        }}
                    >
                        <JoinCreation {...props} />
                    </ProtectedRoute>
                    <ProtectedRoute
                        exact
                        path={`/:locale/${rootPath}/agentic/join-creation`}
                        {...{
                            ...props,
                            authFlow: AUTH_FLOW_TYPES.AGENTIC
                        }}
                    >
                        <AgenticJoinCreation {...props} />
                    </ProtectedRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/join-creation/confirm`}>
                        <EligibilityPage
                            {
                                ...{
                                    ...props,
                                    eligibilityReason: reasons.TIMEOUT_JEL
                                }
                            }
                        />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/add-property/join/timeout`}>
                        <EligibilityPage
                            {
                                ...{
                                    ...props,
                                    eligibilityReason: reasons.TIMEOUT
                                }
                            }
                        />
                    </LocaleRoute>
                    <LocaleRoute exact path={`/:locale/${rootPath}/add-property/ineligible`}>
                        <EligibilityPage
                            {
                                ...{
                                    ...props,
                                    eligibilityReason: reasons.AAP_INELIGIBLE
                                }
                            }
                        />
                    </LocaleRoute>
                    <Route render={() => <Redirect to={`/:locale/${rootPath}`} />} />
                </Switch>
                {showLegalFooter && (
                    <LegalFooter
                        str={legalFooterText}
                        tooltip={legalFooterTooltip}
                    />
                )}
            </Template>
        </LYPContext>
    );
}

ListYourPropertyPage.propTypes = {
    referrer: PropTypes.string.isRequired,
    Template: PropTypes.func.isRequired,
    templateId: PropTypes.string.isRequired,
    rootPath: PropTypes.string.isRequired,
    locale: PropTypes.string.isRequired,
    // eslint-disable-next-line react/forbid-prop-types
    experiments: PropTypes.arrayOf(PropTypes.object).isRequired,
    vrboOwnerAuthUrl: PropTypes.string.isRequired,
    history: PropTypes.shape({
        replace: PropTypes.func,
        location: PropTypes.shape({
            pathname: PropTypes.string
        }).isRequired
    }).isRequired,
    hasAdvertiserForSite: PropTypes.bool.isRequired,
    features: PropTypes.shape({
        isLocationFirst: PropTypes.bool,
        isFrboFlow: PropTypes.bool
    }).isRequired,
    analytics: PropTypes.shape({
        viewTTIstart: PropTypes.func,
        viewTTIstop: PropTypes.func,
        setAnalyticsPageName: PropTypes.func
    }).isRequired,
    legalFooterText: PropTypes.string.isRequired,
    legalFooterTooltip: PropTypes.string.isRequired,
    showLegalFooter: PropTypes.bool.isRequired,
    query: PropTypes.shape({
        source: PropTypes.string
    }).isRequired,
    relativePath: PropTypes.string.isRequired,
    pathSource: PropTypes.string.isRequired,
    setPathSource: PropTypes.func.isRequired,
    hydrationComplete: PropTypes.bool.isRequired
};

/* istanbul ignore next */
export const mapStateToProps = (state, ownProps) => {
    const { app, hydration } = state;
    const { hydrationComplete } = hydration;
    const {
        referrer,
        rootPath,
        locale,
        experiments,
        isLoggedIn,
        vrboOwnerAuthUrl,
        hasAdvertiserForSite,
        features,
        query
    } = app;

    const { isFrboFlow } = features;

    const relativePath = ownProps.location.pathname.replace(new RegExp(`/${locale.display}/${rootPath}`), '');

    const translations = new TranslationUtil(app.translations, app.fallbackTranslations);
    const legalFooterText = translations.getTranslation('lyp.footer.legal');
    const legalFooterTooltip = translations.getTranslation('lyp.footer.tooltip');
    const country = locale.display.slice(-2).toUpperCase();
    const showLegalFooter = (
        requiresLegalFooter.includes(country)
        && isFrboFlow
    );

    let pathSource = pathSources.ULX;
    // PCO redirects user to ULX to valdiate address
    if (relativePath
        && (relativePath.includes('location'))
        && features.isConventionalLodgingFlow) {
        const { source } = query;
        if (source && pathSources[source]) {
            pathSource = pathSources[source];
        }
    }

    if (relativePath?.includes('add-property')) {
        pathSource = pathSources.addPropertyV2;
    }

    const isNonUlxPath = pathSource !== pathSources.ULX && !isFrboFlow;

    const templateId = isNonUlxPath ? templates.EG : app.templateId;

    return {
        showLegalFooter,
        legalFooterText,
        legalFooterTooltip,
        referrer,
        Template: getTemplate(templateId, relativePath, isFrboFlow), // Capitalized for JSX
        rootPath,
        experiments,
        locale: locale.display,
        isLoggedIn,
        vrboOwnerAuthUrl,
        hasAdvertiserForSite,
        features,
        templateId,
        query,
        relativePath,
        hydrationComplete,
        pathSource
    };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch) => ({
    setPathSource: (pathSource) => {
        dispatch(AppActions.setPathSource(pathSource));
    }
});

// eslint-disable-next-line max-len
const ConnectedListYourProperty = connect(mapStateToProps, mapDispatchToProps)(ListYourPropertyPage);
ConnectedListYourProperty.displayName = 'ListYourPropertyPage';

export default hot(module)(withRouter(ConnectedListYourProperty));
